import React, { useEffect, useState } from "react";
import { FieldControl, FormBuilder } from "react-reactive-form";
import { CFormInput, CButton } from "@coreui/react";
import { Validators } from "react-reactive-form";
import LaySummaryButtonGroup from "../components/LaySummaryButtonGroup";
import LaySummaryTgNames from "../components/LaySummaryTgNames";
import { laySummaryQuestionsInfo } from "../utils/LSQuestionsInfo";
import QuestionItemInfo from "../components/QuestionItemInfo";

// Error display component
const FieldError = ({ fieldName, form, showError = false }) => {
  if (!form || !form.controls || !form.controls[fieldName]) {
    return null;
  }

  const field = form.controls[fieldName];

  // Check if field is empty (for required validation)
  let isEmpty = false;

  if (Array.isArray(field.value)) {
    isEmpty = !field.value.some(
      item =>
        item.name != null &&
        item.name !== "" &&
        item.role != null &&
        item.role !== ""
    );
  } else {
    // For simple fields
    isEmpty = !field.value || field.value === "";
  }

  // Show error if explicitly requested OR if field is empty and validation is shown
  const hasError = showError && isEmpty;

  if (!hasError) {
    return null;
  }

  const isRequired = true; // All fields are required

  const errorMessage = isRequired
    ? "This field is required."
    : "Please enter a valid value.";

  return (
    <div
      className="field-error"
      style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.25rem" }}>
      {errorMessage}
    </div>
  );
};

const LSForm = ({ globalId, wizardForm, missingFields }) => {
  const [showValidation, setShowValidation] = useState(false);
  // Function to trigger validation display
  const triggerValidation = () => {
    setShowValidation(true);
    // Mark all fields as touched
    Object.keys(wizardForm.controls).forEach(controlName => {
      wizardForm.controls[controlName].markAsTouched();
    });
  };

  // Function to move cursor to first validation error based on vertical position
  const moveCursorToFirstError = missingFields => {
    if (!missingFields || missingFields.length === 0) {
      return;
    }

    // Define field order based on vertical position on the page (top to bottom)
    const fieldOrder = [
      "qShortTgName",
      "qStudyTitle",
      "qComparative",
      "qTgNames",
      "qStudyPurpose",
      "qFollowUp",
      "qFollowUpDesc",
      "qReferences",
    ];

    // Find the first missing field in the defined order
    const firstErrorField = fieldOrder.find(fieldName =>
      missingFields.includes(fieldName)
    );

    if (firstErrorField) {
      // Use setTimeout to ensure DOM is updated after validation
      setTimeout(() => {
        let elementToFocus = null;

        // Define field-specific selectors based on field type
        const fieldSelectors = {
          // Text input fields (Q1, Q2, Q7.1)
          qShortTgName: [
            `input[name="${firstErrorField}"]`,
            `input[placeholder*="shortened drug"]`,
            `input[placeholder*="treatment name"]`,
          ],
          qStudyTitle: [
            `input[name="${firstErrorField}"]`,
            `input[placeholder*="brief study title"]`,
            `input[placeholder*="study title"]`,
          ],
          qFollowUpDesc: [
            `input[name="${firstErrorField}"]`,
            `input[placeholder*="follow-up study description"]`,
            `input[placeholder*="description"]`,
          ],

          qComparative: [
            `input[id*="${firstErrorField}"][type="radio"]`,
            `[data-field-name="${firstErrorField}"] input[type="radio"]:first-child`,
            `[data-field-name="${firstErrorField}"] input:first-child`,
          ],
          qStudyPurpose: [
            `input[id*="${firstErrorField}"][type="radio"]`,
            `[data-field-name="${firstErrorField}"] input[type="radio"]:first-child`,
            `[data-field-name="${firstErrorField}"] input:first-child`,
          ],
          qFollowUp: [
            `input[id*="${firstErrorField}"][type="radio"]`,
            `[data-field-name="${firstErrorField}"] input[type="radio"]:first-child`,
            `[data-field-name="${firstErrorField}"] input:first-child`,
          ],

          // Complex array field (Q5)
          qTgNames: [
            `[data-field-name="${firstErrorField}"] input[placeholder*="Treatment"]`,
            `[data-field-name="${firstErrorField}"] input[placeholder*="Group name"]`,
            `[data-field-name="${firstErrorField}"] input:first-child`,
            `[data-field-name="${firstErrorField}"] .tg-name-input input`,
          ],

          // References field (Q8)
          qReferences: [
            `input[name="${firstErrorField}"]`,
            `input[placeholder*="reference URL"]`,
            `input[type="url"]`,
          ],
        };

        // Get selectors for the specific field
        const selectors = fieldSelectors[firstErrorField] || [
          `input[name="${firstErrorField}"]`,
          `input[id*="${firstErrorField}"]`,
          `[data-field-name="${firstErrorField}"] input:first-child`,
          `[data-field-name="${firstErrorField}"] input, textarea, select`,
        ];

        // Try each selector until we find an element
        for (const selector of selectors) {
          elementToFocus = document.querySelector(selector);
          if (elementToFocus) {
            break;
          }
        }

        // If still not found, try generic fallbacks
        if (!elementToFocus) {
          // Try to find by field container
          const fieldContainer = document.querySelector(
            `[data-field-name="${firstErrorField}"]`
          );
          if (fieldContainer) {
            elementToFocus = fieldContainer.querySelector(
              "input, textarea, select, button"
            );
          }
        }

        // Final fallback: search for any element with the field name in attributes
        if (!elementToFocus) {
          const allInputs = document.querySelectorAll(
            "input, textarea, select, button"
          );
          for (const input of allInputs) {
            if (
              input.id?.includes(firstErrorField) ||
              input.name?.includes(firstErrorField) ||
              input.className?.includes(firstErrorField)
            ) {
              elementToFocus = input;
              break;
            }
          }
        }

        // Focus and scroll to the element if found
        if (elementToFocus) {
          // Focus the element
          elementToFocus.focus();

          // Scroll into view with smooth behavior
          elementToFocus.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });

          // Add a visual highlight to make it more obvious
          elementToFocus.style.outline = "2px solid #007bff";
          elementToFocus.style.outlineOffset = "2px";

          // Remove the highlight after a few seconds
          setTimeout(() => {
            elementToFocus.style.outline = "";
            elementToFocus.style.outlineOffset = "";
          }, 3000);
        } else {
          console.warn(`Could not find element for field: ${firstErrorField}`);
          // Log all available inputs for debugging
        }
      }, 500);
    }
  };

  // Expose triggerValidation and moveCursorToFirstError functions to parent component
  useEffect(() => {
    if (wizardForm && typeof wizardForm.triggerValidation === "undefined") {
      wizardForm.triggerValidation = triggerValidation;
      wizardForm.moveCursorToFirstError = moveCursorToFirstError;
    }
  }, [wizardForm]);

  // Conditional validation for qFollowUpDesc based on qFollowUp
  useEffect(() => {
    if (
      wizardForm &&
      wizardForm.controls &&
      wizardForm.controls.qFollowUp &&
      wizardForm.controls.qFollowUpDesc
    ) {
      const subscription = wizardForm.controls.qFollowUp.valueChanges.subscribe(
        value => {
          const followUpDescControl = wizardForm.controls.qFollowUpDesc;

          if (value === "true") {
            // If follow-up is "Yes", make qFollowUpDesc required
            followUpDescControl.setValidators(Validators.required);
          } else {
            // If follow-up is "No" or empty, remove required validator
            followUpDescControl.setValidators(null);
          }

          // Update validation status
          followUpDescControl.updateValueAndValidity();
        }
      );

      return () => {
        if (subscription && typeof subscription.unsubscribe === "function") {
          subscription.unsubscribe();
        }
      };
    }
  }, [wizardForm]);

  // Define the choices for button groups
  const studyTypes = [
    { value: "Single-arm", title: "Single-arm" },
    { value: "Multi-arm (comparative)", title: "Multi-arm (comparative)" },
    { value: "Dose finding", title: "Dose finding" },
  ];

  const studyPurposeChoices = [
    { value: "Efficacy", title: "Efficacy" },
    { value: "Safety", title: "Safety" },
    // { value: "PK", title: "PK" },//TODO : to be added later
  ];

  const yesNoChoices = [
    { value: "true", title: "Yes" },
    { value: "false", title: "No" },
  ];

  // Check if follow-up is enabled to show follow-up description
  const showFollowUpDesc = wizardForm.controls.qFollowUp.value === "true";

  return (
    <form name={"laySummaryWizardForm"}>
      {/* Question 1: Short Treatment Name */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            1. Please add the preferred shortened drug/treatment name.
            <span style={{ color: "#dc3545" }}>*</span>
          </span>
        </div>
        <div className={"questions-content__input"}>
          <FieldControl
            name="qShortTgName"
            render={({ handler }) => (
              <CFormInput
                className={`input-text ${showValidation && (!wizardForm.controls.qShortTgName?.value || wizardForm.controls.qShortTgName?.value === "") ? "input-error" : ""}`}
                type="text"
                placeholder="Enter shortened drug/treatment name"
                {...handler()}
                onBlur={e => {
                  handler().onBlur(e);
                  wizardForm.controls.qShortTgName.markAsTouched();
                }}
              />
            )}
          />
          <FieldError
            fieldName="qShortTgName"
            form={wizardForm}
            showError={missingFields.includes("qShortTgName")}
          />
        </div>
      </div>

      {/* Question 2: Study Title */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            2. Please add the brief study title.
            <span style={{ color: "#dc3545" }}>*</span>
          </span>
        </div>
        <div className={"questions-content__input"}>
          <FieldControl
            name="qStudyTitle"
            render={({ handler }) => (
              <CFormInput
                className={`input-text ${showValidation && (!wizardForm.controls.qStudyTitle?.value || wizardForm.controls.qStudyTitle?.value === "") ? "input-error" : ""}`}
                type="text"
                placeholder="Enter brief study title"
                {...handler()}
                onBlur={e => {
                  handler().onBlur(e);
                  wizardForm.controls.qStudyTitle.markAsTouched();
                }}
              />
            )}
          />
          <FieldError
            fieldName="qStudyTitle"
            form={wizardForm}
            showError={missingFields.includes("qStudyTitle")}
          />
        </div>
      </div>

      {/* Question 4: Treatment Group Names */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            3. Please specify the type of study, choosing the option that best
            fits the design.
            <span style={{ color: "#dc3545" }}>*</span>
          </span>
        </div>
        <div className={"questions-content__input"}>
          <LaySummaryButtonGroup
            questionName="qComparative"
            globalId={globalId}
            choices={studyTypes}
          />
          <FieldError
            fieldName="qComparative"
            form={wizardForm}
            showError={missingFields.includes("qComparative")}
          />
        </div>
      </div>

      {/* Question 5: Treatment Group Names */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            4. What are the actual names used to describe the treatment/analysis
            groups in the CSR? Please define the role for each treatment name.
            <span style={{ color: "#dc3545" }}>*</span>
          </span>
        </div>
        <div className={"questions-content__input"}>
          <LaySummaryTgNames formGroup={wizardForm} />
          <FieldError
            fieldName="qTgNames"
            form={wizardForm}
            showError={missingFields.includes("qTgNames")}
            // showError={true}
          />
        </div>
      </div>

      {/* Question 6: Study Purpose */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            5. Is there an efficacy analysis for this study? What was the
            purpose of this study?
            <span style={{ color: "#dc3545" }}>*</span>
          </span>
          <QuestionItemInfo
            questionName="qStudyPurpose"
            notes={laySummaryQuestionsInfo}
          />
        </div>
        <div className={"questions-content__input"}>
          <LaySummaryButtonGroup
            questionName="qStudyPurpose"
            globalId={globalId}
            choices={studyPurposeChoices}
          />
          <FieldError
            fieldName="qStudyPurpose"
            form={wizardForm}
            showError={missingFields.includes("qStudyPurpose")}
          />
        </div>
      </div>

      {/* Question 6: Follow-up Study */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            <span
              dangerouslySetInnerHTML={{
                __html:
                  "6. Was there a follow up study completed for this trial that you'd like to mention in the lay summary?",
              }}></span>
            <span style={{ color: "#dc3545" }}>*</span>
          </span>
        </div>
        <div className={"questions-content__input"}>
          <LaySummaryButtonGroup
            questionName="qFollowUp"
            globalId={globalId}
            choices={yesNoChoices}
          />
          <FieldError
            fieldName="qFollowUp"
            form={wizardForm}
            showError={missingFields.includes("qFollowUp")}
          />
        </div>
      </div>

      {/* Question 7.1: Follow-up Description (conditional) */}
      {showFollowUpDesc && (
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              6.1. Please provide a short description of the completed follow up
              study.
              <span style={{ color: "#dc3545" }}>*</span>
            </span>
            <QuestionItemInfo
              questionName="qFollowUpDesc"
              notes={laySummaryQuestionsInfo}
            />
          </div>
          <div className={"questions-content__input"}>
            <FieldControl
              name="qFollowUpDesc"
              render={({ handler }) => (
                <CFormInput
                  className={`input-text ${showValidation && (!wizardForm.controls.qFollowUpDesc?.value || wizardForm.controls.qFollowUpDesc?.value === "") ? "input-error" : ""}`}
                  type="text"
                  placeholder="Enter follow-up study description"
                  {...handler()}
                  onBlur={e => {
                    handler().onBlur(e);
                    wizardForm.controls.qFollowUpDesc.markAsTouched();
                  }}
                />
              )}
            />
            <FieldError
              fieldName="qFollowUpDesc"
              form={wizardForm}
              showError={missingFields.includes("qFollowUpDesc")}
            />
          </div>
        </div>
      )}

      {/* Question 8: References */}
      <div className={"questions-content__item"}>
        <div className={"questions-content__label"}>
          <span className={"questions-content__label-text"}>
            7. What references would you like to include? Please only include
            the (HTML/HTTPS) link to the reference.
          </span>
        </div>
        <div className={"questions-content__input"}>
          <FieldControl
            name="qReferences"
            render={() => {
              const referencesArray = wizardForm.controls.qReferences;
              const references = referencesArray?.value || [];

              const addReference = () => {
                referencesArray.push(FormBuilder.control(""));
              };

              const removeReference = index => {
                if (references.length > 1) {
                  referencesArray.removeAt(index);
                }
              };

              const updateReference = (index, value) => {
                referencesArray.at(index).setValue(value);
              };

              // Ensure at least one reference field exists
              if (!referencesArray || references.length === 0) {
                if (referencesArray) {
                  referencesArray.push(FormBuilder.control(""));
                }
              }

              return (
                <div className="references-container">
                  {references.map((reference, index) => (
                    <div
                      key={index}
                      className="reference-item"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}>
                      <CFormInput
                        className={`input-text flex-grow-1 ${showValidation && (!reference || reference === "") ? "input-error" : ""}`}
                        type="url"
                        placeholder="Enter reference URL (HTML/HTTPS)"
                        value={reference || ""}
                        onChange={e => updateReference(index, e.target.value)}
                        onBlur={() => {
                          wizardForm.controls.qReferences.markAsTouched();
                        }}
                        style={{ marginRight: "0.5rem" }}
                      />
                      {references.length > 1 && (
                        <CButton
                          color="danger"
                          variant="outline"
                          size="sm"
                          onClick={() => removeReference(index)}
                          style={{
                            minWidth: "auto",
                            padding: "0.375rem 0.75rem",
                          }}>
                          Remove
                        </CButton>
                      )}
                    </div>
                  ))}
                  <CButton
                    color="primary"
                    variant="outline"
                    size="sm"
                    onClick={addReference}
                    style={{ marginTop: "0.5rem" }}>
                    Add another reference
                  </CButton>
                </div>
              );
            }}
          />
          <FieldError
            fieldName="qReferences"
            form={wizardForm}
            showError={showValidation}
          />
        </div>
      </div>
    </form>
  );
};

export default LSForm;
